/**
 * The laptop calendar's per-browser memory (Akash, 2026-10-30):
 *
 *  - VIEW. The calendar opens on the view (Day, Week, Month or Agenda) this
 *    browser last used; Day on a first visit. Only the view: the date
 *    always comes back to today. A `cal=` in the URL always wins, so a
 *    shared link opens exactly what was shared (`calendar-url.ts`).
 *    Phones are not affected: they open on Now.
 *  - DENSITY. Compact rows (one line per robot, close tasks merged into a
 *    count) or comfortable ones (stacked); Compact unless chosen.
 *  - MONTH LEGEND (2026-10-31). The task types the Month grid hides,
 *    chosen by clicking them in its legend. Everything shows unless
 *    hidden; a type added in a later release therefore arrives shown.
 *
 * `localStorage`, in the `nx:<thing>` key style of
 * `state/user-preferences.ts`, and per browser on purpose: a front-desk PC
 * and a supervisor's laptop want different things. Every read and write
 * is guarded -- private mode, a full quota or a blocked store must never
 * take the calendar down; it just forgets.
 */
import { isCalendarViewKey, type CalendarViewKey } from './calendar-url';
import { loadCalendarVisibility, type LanePaint } from '../calendar-categories';
import { LANE_PAINTS } from './lane-style';

export const CAL_VIEW_KEY = 'nx:task-cal-view';
export const LANE_DENSITY_KEY = 'nx:task-cal-density';
export const MONTH_HIDDEN_KEY = 'nx:task-cal-month-hidden';

export type LaneDensity = 'compact' | 'comfy';
export const DEFAULT_LANE_DENSITY: LaneDensity = 'compact';

function read(key: string): string | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value);
  } catch {
    // A lost display preference is not worth failing over.
  }
}

/** The view this browser last used, or null when it has none. */
export function loadCalendarView(): CalendarViewKey | null {
  const v = read(CAL_VIEW_KEY);
  return isCalendarViewKey(v) ? v : null;
}

export function saveCalendarView(view: CalendarViewKey): void {
  write(CAL_VIEW_KEY, view);
}

export function loadLaneDensity(): LaneDensity {
  const v = read(LANE_DENSITY_KEY);
  return v === 'comfy' || v === 'compact' ? v : DEFAULT_LANE_DENSITY;
}

export function saveLaneDensity(density: LaneDensity): void {
  write(LANE_DENSITY_KEY, density);
}

/**
 * The types the Month grid hides. With none chosen yet in this browser,
 * the old Month sidebar's checkboxes for `legacySiteId` carry over
 * (`calendar-categories.ts`, stored per site): a type someone switched off
 * there stays off, and a linen run follows Delivery as it did then.
 */
export function loadMonthHidden(legacySiteId?: string): Set<LanePaint> {
  const raw = read(MONTH_HIDDEN_KEY);
  if (raw === null) {
    if (!legacySiteId) return new Set();
    const old = loadCalendarVisibility(legacySiteId);
    return new Set(LANE_PAINTS.filter((p) => (p === 'linen' ? !old.delivery : !old[p])));
  }
  try {
    const list = JSON.parse(raw) as unknown;
    if (!Array.isArray(list)) return new Set();
    return new Set(LANE_PAINTS.filter((p) => list.includes(p)));
  } catch {
    return new Set();
  }
}

export function saveMonthHidden(hidden: ReadonlySet<LanePaint>): void {
  write(MONTH_HIDDEN_KEY, JSON.stringify(LANE_PAINTS.filter((p) => hidden.has(p))));
}
